// pages/ProfilePage.jsx
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../Auth-Context/AuthContext";
import { changePassword } from "../api/services";
import Sidebar from "../components/Sidebar";
import Topbar from "../components/Topbar";
import {
  KeyRound, Eye, EyeOff,
  CheckCircle2, AlertCircle, Loader2, ArrowLeft,
  User, Mail, Shield, Lock, X
} from "lucide-react";

function RoleBadge({ role }) {
  const map = {
    SUPER_ADMIN: { bg: "rgba(0,157,185,0.1)", color: "#009DB9", label: "Super Admin", dot: "#009DB9" },
    ADMIN:       { bg: "#e0f2fe",              color: "#0284c7", label: "Admin",       dot: "#0284c7" },
    MANAGER:     { bg: "#ede9fe",              color: "#7c3aed", label: "Manager",     dot: "#7c3aed" },
    SALES_REP:   { bg: "#dcfce7",              color: "#16a34a", label: "Sales Rep",   dot: "#16a34a" },
    EMPLOYEE:    { bg: "#fef3c7",              color: "#d97706", label: "Employee",    dot: "#d97706" },
  };
  const style = map[role] ?? map.SALES_REP;
  return (
    <span
      className="inline-flex items-center gap-1.5 text-[11px] font-semibold rounded-full px-3 py-1"
      style={{ background: style.bg, color: style.color }}
    >
      <span className="w-1.5 h-1.5 rounded-full" style={{ background: style.dot }} />
      {style.label}
    </span>
  );
}

function InfoItem({ icon: Icon, label, value }) {
  return (
    <div className="flex items-start gap-3 p-3.5 rounded-xl" style={{ background: "#f8fafb" }}>
      <div className="flex items-center justify-center flex-shrink-0 w-8 h-8 rounded-lg" style={{ background: "rgba(0,157,185,0.08)" }}>
        <Icon size={14} style={{ color: "#009DB9" }} />
      </div>
      <div className="min-w-0">
        <div className="text-[10px] font-semibold text-neutral-400 uppercase tracking-widest mb-0.5">{label}</div>
        <div className="text-[13px] font-semibold text-neutral-800 truncate">{value}</div>
      </div>
    </div>
  );
}

function PasswordField({ label, value, onChange, show, onToggle, placeholder }) {
  return (
    <div>
      <label className="text-[11px] font-semibold text-neutral-500 uppercase tracking-widest mb-1.5 block">{label}</label>
      <div className="relative">
        <input
          type={show ? "text" : "password"}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          className="w-full h-10 border border-neutral-200 rounded-xl px-4 text-[13px] outline-none transition-all pr-10"
          style={{ background: "#fafafa" }}
          onFocus={(e) => { e.currentTarget.style.borderColor = "#009DB9"; e.currentTarget.style.background = "#fff"; }}
          onBlur={(e) => { e.currentTarget.style.borderColor = "#e5e5e5"; e.currentTarget.style.background = "#fafafa"; }}
        />
        <button
          type="button"
          onClick={onToggle}
          className="absolute transition-colors -translate-y-1/2 right-3 top-1/2 text-neutral-400 hover:text-neutral-600"
        >
          {show ? <EyeOff size={14} /> : <Eye size={14} />}
        </button>
      </div>
    </div>
  );
}

export default function ProfilePage() {
  const navigate = useNavigate();
  const { user } = useAuth();

  const [currentPassword, setCurrentPassword]   = useState("");
  const [newPassword, setNewPassword]             = useState("");
  const [confirmPassword, setConfirmPassword]     = useState("");
  const [showCurrent, setShowCurrent]             = useState(false);
  const [showNew, setShowNew]                     = useState(false);
  const [showConfirm, setShowConfirm]             = useState(false);
  const [isLoading, setIsLoading]                 = useState(false);
  const [error, setError]                         = useState("");
  const [success, setSuccess]                     = useState("");

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    if (!currentPassword || !newPassword || !confirmPassword) {
      setError("Please fill in all password fields");
      return;
    }
    if (newPassword !== confirmPassword) {
      setError("New passwords do not match");
      return;
    }
    if (newPassword.length < 6) {
      setError("New password must be at least 6 characters");
      return;
    }

    setIsLoading(true);
    try {
      await changePassword({ currentPassword, newPassword });
      setSuccess("Password changed successfully!");
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
      setTimeout(() => setSuccess(""), 3000);
    } catch (err) {
      setError(err.response?.data?.message || "Failed to change password. Please check your current password.");
      setTimeout(() => setError(""), 4000);
    } finally {
      setIsLoading(false);
    }
  };

  const fullName = user?.name || (user?.firstName && user?.lastName ? `${user.firstName} ${user.lastName}` : "User");
  const email    = user?.email || "—";
  const role     = user?.role  || "SALES_REP";

  const userInitials = user?.firstName && user?.lastName
    ? `${user.firstName[0]}${user.lastName[0]}`.toUpperCase()
    : (email ? email.substring(0, 2).toUpperCase() : "UZ");

  const isAdmin  = role === "SUPER_ADMIN" || role === "ADMIN";
  const isSuperAdmin = role === "SUPER_ADMIN";
  const backPath = isAdmin ? "/admin" : "/dashboard";

  const roleLabel =
    role === "SUPER_ADMIN" ? "Super Admin" :
    role === "ADMIN"       ? "Admin"       :
    role === "MANAGER"     ? "Manager"     : "Sales Representative";

  // The admin variant of this page is rendered inside AdminLayout (see
  // App.jsx), which already provides the admin sidebar + topbar chrome. So
  // for SUPER_ADMIN we render only the page content here — no sales Sidebar
  // and no sales Topbar — to avoid stacking two sidebars.
  const PageBody = (
    <main className="flex-1 px-4 py-5 overflow-y-auto md:px-7">

      {/* Back */}
      <button
        onClick={() => navigate(backPath)}
        className="flex items-center gap-1.5 mb-5 text-[12px] text-neutral-500 hover:text-[#009DB9] transition-colors font-medium"
      >
        <ArrowLeft size={13} />
        Back to {isAdmin ? "Admin Panel" : "Dashboard"}
      </button>

      <div className="grid grid-cols-1 gap-4 xl:grid-cols-[340px_1fr]">

        {/* ── Left Column: Identity Card ── */}
        <div className="flex flex-col gap-4">

          {/* Avatar Card */}
          <div
            className="relative flex flex-col items-center p-6 overflow-hidden text-center bg-white border rounded-2xl border-neutral-100"
            style={{ boxShadow: "0 1px 8px rgba(0,0,0,0.05)" }}
          >
            {/* Subtle top accent strip */}
            <div
              className="absolute top-0 left-0 right-0 h-1 rounded-t-2xl"
              style={{ background: "linear-gradient(90deg, #009DB9 0%, #06d6b4 100%)" }}
            />

            {/* Avatar */}
            <div
              className="w-20 h-20 rounded-full flex items-center justify-center text-white text-[28px] font-bold mt-3 mb-3 shadow-md"
              style={{ background: "linear-gradient(135deg, #009DB9 0%, #007a90 100%)" }}
            >
              {userInitials}
            </div>

            <h2 className="text-[16px] font-bold text-neutral-900 mb-1">{fullName}</h2>
            <p className="text-[12px] text-neutral-400 mb-3 break-all">{email}</p>
            <RoleBadge role={role} />

            {/* Divider */}
            <div className="w-full my-5 border-t border-neutral-100" />

            {/* Info list */}
            <div className="w-full flex flex-col gap-2.5 text-left">
              <InfoItem icon={User}   label="Full Name"      value={fullName} />
              <InfoItem icon={Mail}   label="Email Address"  value={email} />
              <InfoItem icon={Shield} label="Role"           value={roleLabel} />
            </div>
          </div>
        </div>

        {/* ── Right Column: Change Password ── */}
        <div
          className="bg-white border rounded-2xl border-neutral-100"
          style={{ boxShadow: "0 1px 8px rgba(0,0,0,0.05)" }}
        >
          {/* Card Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-neutral-100">
            <div className="flex items-center gap-2.5">
              <div
                className="flex items-center justify-center w-8 h-8 rounded-lg"
                style={{ background: "rgba(0,157,185,0.08)" }}
              >
                <Lock size={14} style={{ color: "#009DB9" }} />
              </div>
              <div>
                <div className="text-[14px] font-bold text-neutral-900">Change Password</div>
                <div className="text-[11px] text-neutral-400">Update your account security credentials</div>
              </div>
            </div>
          </div>

          <form onSubmit={handleChangePassword} className="p-6">
            <div className="flex flex-col max-w-md gap-4">

              <PasswordField
                label="Current Password"
                value={currentPassword}
                onChange={setCurrentPassword}
                show={showCurrent}
                onToggle={() => setShowCurrent(!showCurrent)}
                placeholder="Enter your current password"
              />

              <PasswordField
                label="New Password"
                value={newPassword}
                onChange={setNewPassword}
                show={showNew}
                onToggle={() => setShowNew(!showNew)}
                placeholder="At least 6 characters"
              />

              <PasswordField
                label="Confirm New Password"
                value={confirmPassword}
                onChange={setConfirmPassword}
                show={showConfirm}
                onToggle={() => setShowConfirm(!showConfirm)}
                placeholder="Repeat your new password"
              />

              {/* Password hint */}
              <div className="text-[11px] text-neutral-400 flex items-start gap-1.5 px-1">
                <span className="mt-0.5">💡</span>
                Use at least 6 characters. Mix letters, numbers and symbols for stronger security.
              </div>

              {/* Alert messages */}
              {error && (
                <div className="flex items-center gap-2 bg-red-50 border border-red-100 text-red-600 text-[11px] rounded-xl px-4 py-3">
                  <AlertCircle size={13} className="flex-shrink-0" />
                  <span>{error}</span>
                </div>
              )}
              {success && (
                <div className="flex items-center gap-2 bg-emerald-50 border border-emerald-100 text-emerald-600 text-[11px] rounded-xl px-4 py-3">
                  <CheckCircle2 size={13} className="flex-shrink-0" />
                  <span>{success}</span>
                </div>
              )}

              {/* Actions */}
              <div className="flex items-center gap-3 pt-1">
                <button
                  type="submit"
                  disabled={isLoading}
                  className="flex items-center justify-center gap-2 h-10 px-6 rounded-xl text-[13px] font-semibold text-white transition-all disabled:opacity-50"
                  style={{ background: "#009DB9" }}
                  onMouseEnter={(e) => { if (!isLoading) e.currentTarget.style.background = "#007a90"; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = "#009DB9"; }}
                >
                  {isLoading ? <Loader2 size={14} className="animate-spin" /> : <KeyRound size={14} />}
                  {isLoading ? "Updating…" : "Update Password"}
                </button>

                <button
                  type="button"
                  onClick={() => {
                    setCurrentPassword("");
                    setNewPassword("");
                    setConfirmPassword("");
                    setError("");
                    setSuccess("");
                    navigate(backPath);
                  }}
                  className="flex items-center justify-center gap-1.5 h-10 px-5 rounded-xl text-[13px] font-semibold text-neutral-500 border border-neutral-200 hover:bg-neutral-50 transition-colors"
                >
                  <X size={13} />
                  Cancel
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </main>
  );

  // SUPER_ADMIN variant: rendered inside AdminLayout (App.jsx), so we only
  // return the page body — no sales Sidebar, no sales Topbar.
  if (isSuperAdmin) {
    return <div className="flex flex-col flex-1 min-w-0 overflow-hidden">{PageBody}</div>;
  }

  // Everyone else: sales Sidebar + sales Topbar as before.
  return (
    <div className="flex h-screen overflow-hidden bg-neutral-50">
      <Sidebar />

      <div className="flex flex-col flex-1 overflow-hidden">
        <Topbar
          title="My Profile"
          subtitle="Manage your account information and password"
        />
        {PageBody}
      </div>
    </div>
  );
}